import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';
import { DEVICE_COLORS, PROPERTY_MARKER_COLOR, type DeviceType } from '../lib/deviceColors';
import { PropertyMapSheet, DEVICE_TYPE_LABEL_KEY, type PropertyMapSheetDevice } from './map/PropertyMapSheet';
import type { MapLegendRow } from './map/MapLegend';

/**
 * The page a client opens from a share link (/share/<token>). Rendered by
 * main.tsx INSTEAD of the app: no sign-in, no MSAL, no apiClient — a plain
 * fetch to the one unauthenticated endpoint. Everything shown comes from the
 * server's allow-listed snapshot (server/src/repositories/shareLinks.ts).
 */

interface GPS {
  latitude: number;
  longitude: number;
}

interface SharedDevice {
  type: DeviceType;
  number: string;
  zone_number?: number;
  gps_location: GPS;
}

interface SharedPayload {
  expires_at: string;
  property: { name: string; address: string | null; gps_location: GPS | null };
  devices: SharedDevice[];
  last_inspection_at: string | null;
  company: { name: string | null; logo_url: string | null };
  /** The map image is served by the server (it holds the Maps key). */
  has_map: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: SharedPayload }
  | { kind: 'not_found' }
  | { kind: 'rate_limited' }
  | { kind: 'error' };

const LEGEND_ORDER: DeviceType[] = ['controller', 'zone', 'water_meter', 'double_check'];

export function SharePage({ token }: { token: string }) {
  const { t, i18n } = useTranslation('common');
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [imageReady, setImageReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/public/share/${encodeURIComponent(token)}`, { credentials: 'omit' })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) return setState({ kind: 'not_found' });
        if (res.status === 429) return setState({ kind: 'rate_limited' });
        if (!res.ok) return setState({ kind: 'error' });
        setState({ kind: 'ready', data: (await res.json()) as SharedPayload });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const data = state.kind === 'ready' ? state.data : null;

  useEffect(() => {
    if (data) document.title = data.property.name;
  }, [data]);

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });

  const deviceName = (d: SharedDevice): string => {
    switch (d.type) {
      case 'controller':
        return t('sharePage.device.controller', { n: d.number });
      case 'zone':
        return t('sharePage.device.zone', { n: d.number, z: d.zone_number });
      case 'water_meter':
        return d.number ? t('sharePage.device.meter', { n: d.number }) : t('sharePage.device.meterUnnumbered');
      case 'double_check':
        return t('sharePage.device.backflow', { n: d.number });
    }
  };

  const mapUrl = data?.has_map ? `/api/public/share/${encodeURIComponent(token)}/map` : null;

  if (state.kind !== 'ready' || !data) {
    const msg =
      state.kind === 'loading'
        ? { title: t('sharePage.loading'), body: '' }
        : state.kind === 'not_found'
          ? { title: t('sharePage.notFoundTitle'), body: t('sharePage.notFoundBody') }
          : state.kind === 'rate_limited'
            ? { title: t('sharePage.rateLimited'), body: '' }
            : { title: t('sharePage.error'), body: '' };
    return (
      <div className="min-h-screen bg-page flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface border border-line rounded-xl shadow-sm p-6 text-center">
          <h1 className="text-lg font-semibold text-theme-primary">{msg.title}</h1>
          {msg.body && <p className="text-sm text-theme-secondary mt-2">{msg.body}</p>}
        </div>
      </div>
    );
  }

  const sheetDevices: PropertyMapSheetDevice[] = data.devices.map((d, i) => ({
    id: `${d.type}-${i}`,
    type: d.type,
    name: deviceName(d),
  }));

  const legendRows: MapLegendRow[] = [
    ...(data.property.gps_location
      ? [{ key: 'property', label: t('mapPrint.typeProperty'), color: PROPERTY_MARKER_COLOR, shape: 'circle' as const }]
      : []),
    ...LEGEND_ORDER.filter((type) => data.devices.some((d) => d.type === type)).map((type) => ({
      key: type,
      label: t(DEVICE_TYPE_LABEL_KEY[type]),
      color: DEVICE_COLORS[type],
      shape: 'circle' as const,
    })),
  ];

  return (
    <div className="min-h-screen bg-page py-6 px-4">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="no-print flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-theme-secondary space-y-0.5">
            {data.last_inspection_at && <p>{t('sharePage.lastInspection', { date: fmt(data.last_inspection_at) })}</p>}
            <p>{t('sharePage.expires', { date: fmt(data.expires_at) })}</p>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!!mapUrl && !imageReady}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Printer size={16} />
            {t('sharePage.print')}
          </button>
        </div>
        <div id="report-content" className="map-sheet-page bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
          <PropertyMapSheet
            companyName={data.company.name}
            logoUrl={data.company.logo_url}
            propertyName={data.property.name}
            address={data.property.address}
            printedDateLabel={fmt(new Date().toISOString())}
            mapImageUrl={mapUrl ?? ''}
            mapImageAlt={data.property.name}
            onMapImageLoad={() => setImageReady(true)}
            onMapImageError={() => setImageReady(true)}
            devices={sheetDevices}
            legendRows={legendRows}
          />
        </div>
      </div>
    </div>
  );
}
